import React, { useState, useEffect } from 'react';
import TaskPopup from './taskPopup';
import ProjektDetail from './projektDetail';
import { apiFetch } from './api';

const DashboardHome = ({ user }) => {
  const [daten, setDaten] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offeneTask, setOffeneTask] = useState(null);
  const [offenesProjekt, setOffenesProjekt] = useState(null);
  const [kalenderWoche, setKalenderWoche] = useState(0);
  const [kalenderTasks, setKalenderTasks] = useState([]);

  const pad = (n) => String(n).padStart(2, '0');

  const getMontag = (offset) => {
    const heute = new Date();
    const tag = heute.getDay();
    const diffZuMo = tag === 0 ? -6 : 1 - tag;
    const montag = new Date(heute);
    montag.setDate(heute.getDate() + diffZuMo + offset * 7);
    montag.setHours(0, 0, 0, 0);
    return montag;
  };

  const datumStr = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  const lade = async () => {
    const res = await apiFetch(`/dashboard?user_id=${user.id}`);
    const data = await res.json();
    setDaten(data);
    setLoading(false);
  };

  const ladeKalender = async (offset) => {
    const montag = getMontag(offset);
    const sonntag = new Date(montag);
    sonntag.setDate(montag.getDate() + 6);
    const res = await apiFetch(
      `/meine/tasks/woche?user_id=${user.id}&woche_von=${datumStr(montag)}&woche_bis=${datumStr(sonntag)}`
    );
    const data = await res.json();
    setKalenderTasks(data);
  };

  useEffect(() => { lade(); }, [user.id]);
  useEffect(() => { ladeKalender(kalenderWoche); }, [kalenderWoche, user.id]);

  const formatMin = (min) => {
    const h = Math.floor(min / 60);
    const m = min % 60;
    if (h === 0) return `${m}min`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}min`;
  };

  const statusFarbe = (status) => {
    const f = { offen: '#9b9b9b', 'in arbeit': '#1565c0', erledigt: '#2e7d32' };
    return f[status] || '#9b9b9b';
  };

  const badge = (prio) => {
    const farben = {
      hoch: { bg: '#FCEBEB', text: '#A32D2D' },
      mittel: { bg: '#FAEEDA', text: '#854F0B' },
      niedrig: { bg: '#EAF3DE', text: '#3B6D11' }
    };
    const f = farben[prio] || farben.mittel;
    return (
      <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '20px', backgroundColor: f.bg, color: f.text, textTransform: 'capitalize' }}>
        {prio}
      </span>
    );
  };

  const formatDeadline = (dl) => {
    if (!dl) return '—';
    const d = new Date(dl);
    const tage = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return `${tage[d.getDay()]} ${d.getDate()}.${d.getMonth() + 1}.`;
  };

  const tagNamen = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

  const wochenTage = () => {
    const montag = getMontag(kalenderWoche);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(montag);
      d.setDate(montag.getDate() + i);
      return d;
    });
  };

  const tasksFuerTag = (tagDate) => {
    const tagStr = datumStr(tagDate);
    return kalenderTasks.filter(t => t.von <= tagStr && t.bis >= tagStr);
  };

  const istHeute = (d) => {
    const h = new Date();
    return d.getDate() === h.getDate() && d.getMonth() === h.getMonth() && d.getFullYear() === h.getFullYear();
  };

  if (offenesProjekt) {
    return <ProjektDetail projektId={offenesProjekt} onZurueck={() => { setOffenesProjekt(null); lade(); }} />;
  }

  if (loading) return <div style={{ padding: '40px', color: '#9b9b9b', fontSize: '14px' }}>Lädt...</div>;

  const sektionLabel = (t) => (
    <p style={{ fontSize: '11px', fontWeight: '500', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '12px' }}>{t}</p>
  );

  const taskZeile = (t, zeigeDeadline) => (
    <div key={t.id} onClick={() => setOffeneTask(t.id)} style={{
      display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', paddingLeft: '10px',
      borderBottom: '1px solid #f4f4f4', cursor: 'pointer',
      borderLeft: t.mutter_farbe ? `3px solid ${t.mutter_farbe}` : '3px solid transparent',
    }}>
      <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: statusFarbe(t.status), flexShrink: 0 }} />
      <span style={{ fontSize: '14px', color: '#1a1a1a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
      <span style={{ fontSize: '12px', color: '#c4c4c4', flexShrink: 0 }}>{t.mutter_name} · {t.projekt_name}</span>
      {badge(t.prioritaet)}
      {zeigeDeadline && <span style={{ fontSize: '12px', color: '#9b9b9b', minWidth: '60px', textAlign: 'right', flexShrink: 0 }}>{formatDeadline(t.deadline)}</span>}
    </div>
  );

  const tasksDeadlineDeseWoche = [...daten.tasksHeute, ...daten.tasksDieseWoche].filter(t => t.status !== 'erledigt');
  const maxMinuten = Math.max(...daten.stunden.map(s => s.minuten), 1);
  const tage = wochenTage();

  return (
    <div style={{ padding: '40px' }}>
      <div style={{ marginBottom: '28px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 6px 0', letterSpacing: '-0.5px' }}>
          Willkommen zurück, {user.name} 👋
        </h2>
        <p style={{ fontSize: '14px', color: '#9b9b9b', margin: 0 }}>Dein Überblick für heute</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '36px' }}>
        {[
          { label: 'Heute getrackt', value: formatMin(daten.stundenHeute) },
          { label: 'Diese Woche', value: formatMin(daten.stundenWoche) },
          { label: 'Offene Tasks diese Woche', value: String(tasksDeadlineDeseWoche.length) },
        ].map((card, i) => (
          <div key={i} style={{ backgroundColor: '#ffffff', borderRadius: '10px', padding: '24px', border: '1px solid #efefef' }}>
            <p style={{ fontSize: '12px', color: '#9b9b9b', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{card.label}</p>
            <p style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a', margin: 0, letterSpacing: '-1px' }}>{card.value}</p>
          </div>
        ))}
      </div>

      {daten.tasksHeute.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          {sektionLabel('Meine Tasks — heute fällig')}
          <div>{daten.tasksHeute.map(t => taskZeile(t, false))}</div>
        </div>
      )}

      {daten.tasksDieseWoche.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          {sektionLabel('Meine Tasks — bis Ende der Woche')}
          <div>{daten.tasksDieseWoche.map(t => taskZeile(t, true))}</div>
        </div>
      )}

      {daten.projekteDieseWoche.length > 0 && (
        <div style={{ marginBottom: '32px' }}>
          {sektionLabel('Projekte mit Deadline diese/nächste Woche')}
          <div>
            {daten.projekteDieseWoche.map(p => (
              <div key={p.id} onClick={() => setOffenesProjekt(p.id)} style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 0', paddingLeft: '10px',
                borderBottom: '1px solid #f4f4f4', cursor: 'pointer',
                borderLeft: p.mutter_farbe ? `3px solid ${p.mutter_farbe}` : '3px solid transparent',
              }}>
                <span style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: statusFarbe(p.status), flexShrink: 0 }} />
                <span style={{ fontSize: '14px', color: '#1a1a1a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.name}</span>
                <span style={{ fontSize: '12px', color: '#c4c4c4', flexShrink: 0 }}>{p.mutter_name}</span>
                <span style={{ fontSize: '12px', color: '#9b9b9b', minWidth: '60px', textAlign: 'right', flexShrink: 0 }}>{formatDeadline(p.deadline)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {daten.tasksHeute.length === 0 && daten.tasksDieseWoche.length === 0 && daten.projekteDieseWoche.length === 0 && (
        <div style={{ backgroundColor: '#ffffff', borderRadius: '10px', padding: '40px', border: '1px solid #efefef', textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ fontSize: '14px', color: '#9b9b9b', margin: 0 }}>Diese Woche steht nichts Dringendes an. 🎉</p>
        </div>
      )}

      {/* Mini-Wochenkalender */}
      <div style={{ marginBottom: '32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          {sektionLabel('Meine Planung')}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '12px' }}>
            <span onClick={() => setKalenderWoche(0)} style={{
              fontSize: '12px', padding: '4px 12px', borderRadius: '20px', cursor: 'pointer',
              border: '1px solid ' + (kalenderWoche === 0 ? '#1a1a1a' : '#e8e8e8'),
              backgroundColor: kalenderWoche === 0 ? '#1a1a1a' : 'transparent',
              color: kalenderWoche === 0 ? '#fff' : '#6b6b6b'
            }}>Diese Woche</span>
            <span onClick={() => setKalenderWoche(1)} style={{
              fontSize: '12px', padding: '4px 12px', borderRadius: '20px', cursor: 'pointer',
              border: '1px solid ' + (kalenderWoche === 1 ? '#1a1a1a' : '#e8e8e8'),
              backgroundColor: kalenderWoche === 1 ? '#1a1a1a' : 'transparent',
              color: kalenderWoche === 1 ? '#fff' : '#6b6b6b'
            }}>Nächste Woche</span>
          </div>
        </div>

        <div style={{ backgroundColor: '#ffffff', border: '1px solid #efefef', borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #f4f4f4' }}>
            {tage.map((d, i) => (
              <div key={i} style={{ padding: '10px 8px', textAlign: 'center', borderRight: i < 6 ? '1px solid #f4f4f4' : 'none' }}>
                <div style={{ fontSize: '10px', color: '#9b9b9b', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{tagNamen[i]}</div>
                <div style={{
                  fontSize: '14px', fontWeight: '600', marginTop: '2px',
                  color: istHeute(d) ? '#ffffff' : '#1a1a1a',
                  backgroundColor: istHeute(d) ? '#1a1a1a' : 'transparent',
                  borderRadius: '50%', width: '24px', height: '24px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '2px auto 0'
                }}>{d.getDate()}</div>
              </div>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', minHeight: '80px' }}>
            {tage.map((d, i) => {
              const tagTasks = tasksFuerTag(d);
              return (
                <div key={i} style={{
                  padding: '8px 6px', borderRight: i < 6 ? '1px solid #f4f4f4' : 'none',
                  display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, overflow: 'hidden'
                }}>
                  {tagTasks.length === 0 ? <div style={{ height: '4px' }} /> : (
                    tagTasks.map(t => (
                      <div key={t.id} onClick={() => setOffeneTask(t.id)} style={{
                        backgroundColor: t.mutter_farbe ? `${t.mutter_farbe}22` : '#f0f0ee',
                        borderLeft: t.mutter_farbe ? `3px solid ${t.mutter_farbe}` : '3px solid #d4d4d4',
                        borderRadius: '4px', padding: '4px 6px',
                        cursor: 'pointer', fontSize: '11px', color: '#1a1a1a', lineHeight: '1.3',
                        minWidth: 0, overflow: 'hidden'
                      }}>
                        <div style={{ fontWeight: '600', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</div>
                        <div style={{ color: '#6b6b6b', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '10px' }}>{t.projekt_name}</div>
                        <div style={{ color: '#9b9b9b', fontSize: '10px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.mutter_name}</div>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div>
        {sektionLabel('Arbeitsstunden diese Woche')}
        <div style={{ backgroundColor: '#ffffff', borderRadius: '10px', padding: '20px 24px', border: '1px solid #efefef' }}>
          {daten.stunden.map(s => {
            const istIch = s.id === user.id;
            return (
              <div key={s.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '7px 0' }}>
                <div style={{
                  width: '26px', height: '26px', borderRadius: '50%', flexShrink: 0,
                  backgroundColor: istIch ? '#1a1a1a' : '#e4e4e1',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '11px', fontWeight: '600', color: istIch ? '#fff' : '#6b6b6b'
                }}>{s.name.charAt(0).toUpperCase()}</div>
                <span style={{ fontSize: '13px', flex: '0 0 160px', color: istIch ? '#1a1a1a' : '#6b6b6b', fontWeight: istIch ? '600' : '400' }}>
                  {s.name}{istIch ? ' (du)' : ''}
                </span>
                <div style={{ flex: 1, height: '6px', backgroundColor: '#f0f0ee', borderRadius: '3px', overflow: 'hidden' }}>
                  <div style={{ height: '6px', borderRadius: '3px', backgroundColor: istIch ? '#1a1a1a' : '#b4b2a9', width: `${(s.minuten / maxMinuten) * 100}%` }} />
                </div>
                <span style={{ fontSize: '12px', color: '#9b9b9b', minWidth: '60px', textAlign: 'right' }}>{formatMin(s.minuten)}</span>
              </div>
            );
          })}
        </div>
      </div>

      {offeneTask && (
        <TaskPopup taskId={offeneTask} onClose={() => { setOffeneTask(null); lade(); ladeKalender(kalenderWoche); }} />
      )}
    </div>
  );
};

export default DashboardHome;